<template lang="pug">
#account
  .page-controls
    button.button.is-success(
      :class="{ 'is-loading': settings.submitting }"
      @click="submit"
    ) Save Changes
  spinner(v-if="settings.loading")
  b-tabs.page-tabs(
    v-else
    :animated="false"
    position="is-centered"
  )
    b-tab-item(label="Settings")
      .columns.is-multiline
        .column.is-half
          .card
            .card-header
              p.card-header-title Organization
            .card-content
              b-field(label="Name")
                b-input(
                  :value="account.organization"
                  v-validate="'required'"
                  @input="set($event, 'organization')"
                )
              b-field(label="Alternate/Short Name")
                b-input(
                  :value="account.organization_short"
                  @input="set($event, 'organization_short')"
                )
              b-field(label="Abbreviation")
                b-input(
                  :value="account.abbreviation"
                  @input="set($event, 'abbreviation')"
                )
              b-field(label="Tagline")
                b-input(
                  :value="account.tagline"
                  @input="set($event, 'tagline')"
                )
        .column.is-half
          .card
            .card-header
              p.card-header-title Account Configuration
            .card-content
              b-field(label="Host")
                b-input(
                  :value="account.host"
                  disabled
                )
              b-field(label="Sender Email")
                b-input(
                  :value="account.sender_email"
                  v-validate="{ email: true, accountEmail: { domain: account.host } }"
                  @input="set($event, 'sender_email')"
                )
              b-field(label="Twilio Phone Number")
                b-input(
                  :value="account.twilio_phone_number"
                  @input="set($event, 'twilio_phone_number')"
                )
              b-field(label="API Key")
                reveal-field(
                  field="api_key"
                  resource="accounts"
                  :value="account"
                )
              b-field(label="API Secret")
                reveal-field(
                  field="api_secret"
                  resource="accounts"
                  :value="account"
                )
        .column.is-half
          .card
            .card-header
              p.card-header-title Contact
            .card-content
              b-field(label="Contact Email")
                b-input(
                  icon="email"
                  v-validate="'required|email'"
                  :value="account.email"
                  @input="set($event, 'email')"
                )
              b-field(label="Contact Phone Number")
                phone-field(
                  v-validate="'required'"
                  :value="account.phone_number"
                  @input="set($event, 'phone_number')"
                )
              b-field(label="Address")
                b-input(
                  icon="map-marker"
                  :value="address.number_and_route"
                  @input="set($event, '_embedded.location.address.number_and_route')"
                )
              b-field(grouped)
                b-field(label="City" expanded)
                  b-input(
                    :value="address.locality"
                    @input="set($event, '_embedded.location.address.locality')"
                  )
                b-field(label="State" expanded)
                  b-input(
                    :value="address.administrative_area_level_1"
                    @input="set($event, '_embedded.location.address.administrative_area_level_1')"
                  )
                b-field(label="Zipcode" expanded)
                  b-input(
                    :value="address.postal_code"
                    @input="set($event, '_embedded.location.address.postal_code')"
                  )
              a.button.is-primary(
                :class="{ 'is-loading': settings.geocoding }"
                @click="geocode"
              ) Link with Google Maps
              google-map(
                :location="account._embedded.location"
                :name="account._embedded.location.id"
                :refresh="refreshMap"
              )
        .column.is-half
          .card
            .card-header
              p.card-header-title Links
            .card-content
              b-field(label="PushPay")
                b-input(
                  icon="cash-usd"
                  :value="account.giving_link"
                  @input="set($event, 'giving_link')"
                )
              b-field(label="Facebook")
                b-input(
                  icon="facebook"
                  :value="account.social.facebook"
                  @input="set($event, 'social.facebook')"
                )
              b-field(label="Instagram")
                b-input(
                  icon="instagram"
                  :value="account.social.instagram"
                  @input="set($event, 'social.instagram')"
                )
              b-field(label="Twitter")
                b-input(
                  icon="twitter"
                  :value="account.social.twitter"
                  @input="set($event, 'social.twitter')"
                )
              b-field(label="YouTube")
                b-input(
                  icon="youtube"
                  :value="account.social.youtube"
                  @input="set($event, 'social.youtube')"
                )
    b-tab-item(label="Images")
      #images-tab
        b-upload(
          v-model="uploads"
          @input="saveImage"
          multiple
          drag-drop
        )
          section.section
            .content.has-text-centered
              b-icon(icon="image-multiple" size="is-large")
              p Drop an image
              progress.progress.is-small.is-primary(
                :value="settings.uploading"
                max="100"
                v-show="settings.uploading > 0"
              )
        gallery-uploads(
          :value="account._embedded.sub_page_gallery"
          @delete="deleteImage"
          @sort="sortImages"
        )
</template>

<script>
/* global google */
import { mapGetters } from 'vuex';
import { HTTP } from '@/utils/http';
import GoogleMap from '@/components/GoogleMap';
import GalleryUploads from '@/components/GalleryUploads';
import PhoneField from '@/components/form/PhoneField';
import RevealField from '@/components/RevealField';
import Spinner from '@/components/Spinner';
import * as actions from '@/store/types/actions';
import * as getters from '@/store/types/getters';
import * as mutations from '@/store/types/mutations';

export default {
  name: 'account',
  components: {
    GalleryUploads,
    GoogleMap,
    PhoneField,
    RevealField,
    Spinner,
  },
  computed: {
    ...mapGetters({
      account: getters.GET_ACCOUNT,
    }),
    latLon() {
      if (_.isEmpty(this.address.lat_lng)) return null;
      return new google.maps.LatLng(
        this.address.lat_lng[0],
        this.address.lat_lng[1],
      );
    },
    address() {
      if (!this.account._embedded.location) return { address: {} };
      return this.account._embedded.location.address;
    },
  },
  async created() {
    this.settings.loading = true;
    await this.$store.dispatch(actions.DO_GET_ACCOUNT, {
      id: this.account.id,
      zoom: 'location,sub_page_gallery',
    });
    this.settings.loading = false;
  },
  data() {
    return {
      geocoder: null,
      refreshMap: false,
      settings: {
        geocoding: false,
        loading: false,
        uploading: false,
        submitting: false,
      },
      uploads: [],
    };
  },
  methods: {
    async deleteImage(index) {
      const key = 'sub_page_gallery';
      const image = this.account._embedded[key][index];
      this.$store.dispatch(actions.DO_DELETE_MEDIA, {
        id: image.id,
      });
      this.$store.commit(mutations.REMOVE_FROM_GALLERY_FOR_ACCOUNT, {
        key,
        index,
      });
    },
    geocode() {
      this.settings.geocoding = true;
      const components = _.compact([
        this.address.number_and_route,
        this.address.locality,
        this.address.administrative_area_level_1,
        this.address.postal_code,
      ]);
      const address = _.map(components, str => str).join(', ');
      this.geocoder.geocode({
        address,
      }, this.geocodeCallback);
    },
    async geocodeCallback(results, status) {
      if (status === 'OK') {
        const { location } = results[0].geometry;
        const latLng = [location.lat(), location.lng()];
        this.set(latLng, '_embedded.location.address.lat_lng');
        await this.submit();
        this.settings.geocoding = false;
      } else {
        this.settings.geocoding = false;
        this.$store.commit(mutations.SET_APP_SNACKBAR, {
          type: 'is-danger',
          message: 'Invalid address',
        });
      }
    },
    async saveImage() {
      this.settings.uploading = 0;
      const self = this;
      const payload = new FormData();
      payload.append('format', 'multipart');
      payload.append('file', this.uploads[this.uploads.length - 1]);
      const config = {
        onUploadProgress(progressEvent) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          self.settings.uploading = progress;
        },
      };
      const { data } = await HTTP.post('/media/images', payload, config);
      this.$store.commit(mutations.ADD_TO_GALLERY_FOR_ACCOUNT, {
        key: 'sub_page_gallery',
        val: data.data.images[0],
      });
    },
    set(val, key) {
      this.$store.commit(mutations.SET_ACCOUNT_FIELD, {
        key,
        val,
      });
    },
    sortImages(val) {
      this.$store.commit(mutations.SORT_GALLERY_FOR_ACCOUNT, {
        key: 'sub_page_gallery',
        from: val.from,
        to: val.to,
      });
    },
    async submit() {
      this.settings.submitting = true;
      const payload = {
        id: this.account.id,
        abbreviation: this.account.abbreviation,
        email: this.account.email,
        giving_link: this.account.giving_link,
        organization: this.account.organization,
        organization_short: this.account.organization_short,
        phone_number: this.account.phone_number,
        sender_email: this.account.sender_email,
        social_facebook: this.account.social.facebook,
        social_instagram: this.account.social.instagram,
        social_twitter: this.account.social.twitter,
        social_youtube: this.account.social.youtube,
        sub_page_gallery_ids: _.map(this.account._embedded.sub_page_gallery, 'id').join(','),
        tagline: this.account.tagline,
        twilio_phone_number: this.account.twilio_phone_number,
        zoom: 'location,sub_page_gallery',
      };
      const { location } = this.account._embedded;
      const locationAction = location.id === undefined ? actions.DO_CREATE_LOCATION : actions.DO_UPDATE_LOCATION;
      if (location.address.number_and_route) {
        const { data } = await this.$store.dispatch(locationAction, {
          id: location.id,
          address: {
            number_and_route: location.address.number_and_route,
            locality: location.address.locality,
            administrative_area_level_1: location.address.administrative_area_level_1,
            postal_code: location.address.postal_code,
            lat_lng: location.address.lat_lng,
          },
          label: this.account.organization,
        });
        payload.location_id = data.locations[0].id;
      }
      const response = await this.$store.dispatch(actions.DO_UPDATE_ACCOUNT, payload);
      this.settings.submitting = false;
      this.refreshMap = true;
      this.$store.commit(mutations.SET_APP_SNACKBAR, {
        type: 'is-success',
        message: response.message,
      });
      setTimeout(() => {
        this.refreshMap = false;
      }, 1000);
    },
  },
  mounted() {
    this.geocoder = new google.maps.Geocoder();
    // const map = new google.maps.Map(document.getElementById('account-location-preview'));
    // this.placesService = new google.maps.places.PlacesService(map);
  },
};
</script>

<style lang="scss">
#account {
  .section {
    padding: 0 1rem 6rem 1rem;
  }
  .page-controls {
    display: flex;
    justify-content: flex-end;
  }
  .card {
    box-shadow: none;
    border: 1px solid #dbdbdb;
    height: 100%;
    .card-header {
      box-shadow: none;
      border-bottom: 1px solid #dbdbdb;
      .card-header-title {
        font-weight: 400;
      }
    }
  }
  .google-map {
    margin-top: 1rem;
    width: 100%;
    height: 260px;
  }
  #images-tab {
    padding: 0 1rem;
    .upload {
      section.section {
        padding: 1rem;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Account.vue